import {
  OrderPointDto,
  PaymentTypeEnum,
  ServiceTypeEnum,
  LocationPointDto,
  OrderPointAreaDto,
  ClientPromocodeDto,
  ClientOnlinePaymentDto,
  PromocodeDiscountInfoDto,
} from '../api/restApi';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DeliveryOptionEnum, ScheduleDateWithIntervals } from '@/entities';

export interface IOrderOptions {
  eatAtThePlace: boolean;
  leaveAtTheDoor: boolean;
  cutleryQuantity: number;
}

export interface NationwideInfoState {
  city: string;
  price: number;
  locationId: string;
}

export interface OrderDeliveryState {
  areManualCoordinates: boolean;
  city: string;
  street: string;
  house: string;
  floor: string;
  intercom: string;
  apartment: string;
  entranceway: string;
  pointLabel: string;

  price?: number;
  zoneId?: string;
  locationId?: string;
  address?: string;
  coords?: LocationPointDto;
  cityCoords?: LocationPointDto;
  paymentTypes?: PaymentTypeEnum[];
  nationwideInfo?: NationwideInfoState;
  onlinePayment?: ClientOnlinePaymentDto;
}

export const initialOrderDeliveryState: OrderDeliveryState = {
  areManualCoordinates: false,
  city: '',
  street: '',
  house: '',
  floor: '',
  intercom: '',
  apartment: '',
  entranceway: '',
  pointLabel: '',
};

export interface OrderPointDeliveryState {
  userOrderPoint?: OrderPointDto;
  orderPointCity?: { city: string; fullVariant: string };
  orderPointAddress?: string;
  orderPointCityCoords?: LocationPointDto;
  orderPointArea?: OrderPointAreaDto;

  locationId?: string;
  deliveryPrice?: number;
  minCalendarDays?: number;
  maxCalendarDays?: number;
  paymentTypes?: PaymentTypeEnum[];
  onlinePayment?: ClientOnlinePaymentDto;
}

interface OrderPromocodeState {
  promocode: ClientPromocodeDto;
  discount?: PromocodeDiscountInfoDto;
}

export const initialOrderPointDeliveryState: OrderPointDeliveryState = {
  userOrderPoint: undefined,
  orderPointAddress: '',
  orderPointCity: undefined,
  orderPointCityCoords: undefined,
  orderPointArea: undefined,
};

export interface OrderState {
  name: string;
  phone: string;
  email: string;
  commentary: string;
  scheduleSlots: ScheduleDateWithIntervals[];
  infoLoading: boolean;
  noCallNeeded: boolean;
  takeoutAddress?: string;
  fulfillmentToTime?: string;
  deliveryType: ServiceTypeEnum;
  deliveryOption: DeliveryOptionEnum;
  paymentType?: PaymentTypeEnum;
  delivery: OrderDeliveryState;
  options: IOrderOptions;
  orderPointDelivery: OrderPointDeliveryState;
  promocode?: OrderPromocodeState;
  promoText?: string;
  promoLoading: boolean;
}

const initialState: OrderState = {
  name: '',
  phone: '',
  email: '',
  scheduleSlots: [],
  commentary: '',
  infoLoading: false,
  noCallNeeded: false,
  delivery: initialOrderDeliveryState,
  deliveryType: ServiceTypeEnum.Delivery,
  deliveryOption: DeliveryOptionEnum.Courier,
  orderPointDelivery: initialOrderPointDeliveryState,
  options: { cutleryQuantity: 1, eatAtThePlace: false, leaveAtTheDoor: false },
  promocode: undefined,
  promoText: undefined,
  promoLoading: false,
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,

  reducers: {
    setName(state, { payload }: PayloadAction<string>) {
      state.name = payload;
    },
    setPhone(state, { payload }: PayloadAction<string>) {
      state.phone = payload;
    },
    setEmail(state, { payload }: PayloadAction<string>) {
      state.email = payload;
    },
    setNoCallNeeded(state, { payload }: PayloadAction<boolean>) {
      state.noCallNeeded = payload;
    },
    setFloor(state, { payload }: PayloadAction<string>) {
      state.delivery.floor = payload;
    },
    setIntercom(state, { payload }: PayloadAction<string>) {
      state.delivery.intercom = payload;
    },
    setApartement(state, { payload }: PayloadAction<string>) {
      state.delivery.apartment = payload;
    },
    setCommentary(state, { payload }: PayloadAction<string>) {
      state.commentary = payload;
    },
    setEntranceway(state, { payload }: PayloadAction<string>) {
      state.delivery.entranceway = payload;
    },
    setPointLabel(state, { payload }: PayloadAction<string>) {
      state.delivery.pointLabel = payload;
    },

    clearDeliveryAddressDetails(state) {
      state.delivery.city = '';
      state.delivery.street = '';
      state.delivery.house = '';
      state.delivery.floor = '';
      state.delivery.intercom = '';
      state.delivery.apartment = '';
      state.delivery.entranceway = '';
      state.delivery.pointLabel = '';
    },

    setOrderInfoLoading(state, { payload }: PayloadAction<boolean>) {
      state.infoLoading = payload;
    },
    setDeliveryAddress(
      state,
      {
        payload,
      }: PayloadAction<{
        address?: string;
        coordinates?: LocationPointDto;
        city: string;
        street: string;
        house: string;
      }>,
    ) {
      state.delivery.address = payload.address;
      state.delivery.coords = payload.coordinates;
      state.delivery.city = payload.city;
      state.delivery.street = payload.street;
      state.delivery.house = payload.house;
    },
    setDeliveryAddressComponents(state, { payload }: PayloadAction<{ city: string; street: string; house: string }>) {
      state.delivery.city = payload.city;
      state.delivery.street = payload.street;
      state.delivery.house = payload.house;
    },
    setDeliveryAddressAndCoords(
      state,
      {
        payload,
      }: PayloadAction<{
        address?: string;
        coordinates?: LocationPointDto;
      }>,
    ) {
      state.delivery.address = payload.address;
      state.delivery.coords = payload.coordinates;
    },
    setDeliveryOrderPointCity(
      state,
      {
        payload,
      }: PayloadAction<{
        city: string;
        fullCity: string;
        coordsCity?: LocationPointDto;
        coordsArea?: OrderPointAreaDto;
      }>,
    ) {
      state.orderPointDelivery.orderPointCity = { city: payload.city, fullVariant: payload.fullCity };
      state.orderPointDelivery.orderPointCityCoords = payload.coordsCity;
      state.orderPointDelivery.orderPointArea = payload.coordsArea;
    },
    setDeliveryOrderPointAddress(state, { payload }: PayloadAction<string | undefined>) {
      state.orderPointDelivery.orderPointAddress = payload;
    },
    setUserOrderPoint(state, { payload }: PayloadAction<OrderPointDto | undefined>) {
      state.orderPointDelivery.userOrderPoint = payload;
    },
    setOrderPointLocationInfo(
      state,
      {
        payload,
      }: PayloadAction<{
        locationId?: string;
        deliveryPrice?: number;
        minCalendarDays?: number;
        maxCalendarDays?: number;
        paymentTypes?: PaymentTypeEnum[];
        onlinePayment?: ClientOnlinePaymentDto;
      }>,
    ) {
      state.orderPointDelivery.locationId = payload?.locationId;
      state.orderPointDelivery.deliveryPrice = payload?.deliveryPrice;
      state.orderPointDelivery.minCalendarDays = payload?.minCalendarDays;
      state.orderPointDelivery.maxCalendarDays = payload?.maxCalendarDays;
      state.orderPointDelivery.paymentTypes = payload?.paymentTypes;
      state.orderPointDelivery.onlinePayment = payload?.onlinePayment;
    },

    clearOrderPointLocationInfo(state) {
      state.orderPointDelivery.locationId = undefined;
      state.orderPointDelivery.deliveryPrice = undefined;
      state.orderPointDelivery.minCalendarDays = undefined;
      state.orderPointDelivery.maxCalendarDays = undefined;
      state.orderPointDelivery.paymentTypes = undefined;
      state.orderPointDelivery.onlinePayment = undefined;
    },
    clearDeliveryAddress(state) {
      state.delivery.coords = undefined;
      state.delivery.address = undefined;
    },
    setAreManualCoordinates(state, { payload }: PayloadAction<boolean>) {
      state.delivery.areManualCoordinates = payload;
    },

    setNationwideInfo(state, { payload }: PayloadAction<NationwideInfoState | undefined>) {
      state.delivery.nationwideInfo = payload;
    },

    setDeliveryZone(
      state,
      {
        payload,
      }: PayloadAction<{
        price?: number;
        zoneId?: string;
        locationId?: string;
        paymentTypes?: PaymentTypeEnum[];
        onlinePayment?: ClientOnlinePaymentDto;
      }>,
    ) {
      state.delivery.price = payload.price;
      state.delivery.zoneId = payload.zoneId;
      state.delivery.locationId = payload.locationId;
      state.delivery.paymentTypes = payload.paymentTypes;
      state.delivery.onlinePayment = payload.onlinePayment;
    },
    clearDeliveryZone(state) {
      state.delivery.price = undefined;
      state.delivery.zoneId = undefined;
      state.delivery.locationId = undefined;
      state.delivery.paymentTypes = undefined;
      state.delivery.onlinePayment = undefined;
    },

    setTakeoutAddress(state, { payload }: PayloadAction<string | undefined>) {
      state.takeoutAddress = payload;
    },
    setDeliveryType(state, { payload }: PayloadAction<ServiceTypeEnum>) {
      state.deliveryType = payload;
    },
    setDeliveryOption(state, { payload }: PayloadAction<DeliveryOptionEnum>) {
      state.deliveryOption = payload;
    },
    setPaymentType(state, { payload }: PayloadAction<PaymentTypeEnum | undefined>) {
      state.paymentType = payload;
    },

    setCityCoords(state, { payload }: PayloadAction<LocationPointDto | undefined>) {
      state.delivery.cityCoords = payload;
    },

    setScheduleSlots(state, { payload }: PayloadAction<ScheduleDateWithIntervals[]>) {
      state.scheduleSlots = payload;
    },
    setFulfillmentToTime(state, { payload }: PayloadAction<string | undefined>) {
      state.fulfillmentToTime = payload;
    },

    increaseCutleryQuantity(state) {
      state.options.cutleryQuantity += 1;
    },
    decreaseCutleryQuantity(state) {
      state.options.cutleryQuantity -= 1;
    },

    changeEatAtPlace(state, { payload }: PayloadAction<boolean>) {
      state.options.eatAtThePlace = payload;
    },
    changeLeaveAtTheDoor(state, { payload }: PayloadAction<boolean>) {
      state.options.leaveAtTheDoor = payload;
    },

    clearOrder(state) {
      state.options = initialState.options;
      state.promocode = initialState.promocode;
      state.promoText = initialState.promoText;
      state.commentary = initialState.commentary;
      state.fulfillmentToTime = initialState.fulfillmentToTime;
    },
    setPromocode(state, { payload }: PayloadAction<OrderPromocodeState | undefined>) {
      state.promocode = payload;
    },
    setPromoText(state, { payload }: PayloadAction<string | undefined>) {
      state.promoText = payload;
    },
    setPromoLoading(state, { payload }: PayloadAction<boolean>) {
      state.promoLoading = payload;
    },
  },
});

export const {
  setName,
  setPhone,
  setEmail,
  setFloor,
  clearOrder,
  setIntercom,
  setPromocode,
  setPromoText,
  setApartement,
  setCommentary,
  setCityCoords,
  setEntranceway,
  setPointLabel,
  setPaymentType,
  setPromoLoading,
  setNoCallNeeded,
  setDeliveryType,
  setDeliveryZone,
  setScheduleSlots,
  changeEatAtPlace,
  setUserOrderPoint,
  setNationwideInfo,
  clearDeliveryZone,
  setTakeoutAddress,
  setDeliveryOption,
  setDeliveryAddress,
  setOrderInfoLoading,
  clearDeliveryAddress,
  setFulfillmentToTime,
  changeLeaveAtTheDoor,
  increaseCutleryQuantity,
  decreaseCutleryQuantity,
  setAreManualCoordinates,
  setDeliveryOrderPointCity,
  setOrderPointLocationInfo,
  clearOrderPointLocationInfo,
  setDeliveryAddressAndCoords,
  clearDeliveryAddressDetails,
  setDeliveryOrderPointAddress,
  setDeliveryAddressComponents,
} = orderSlice.actions;

export const orderReducer = orderSlice.reducer;
